/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, Box, DialogTitle, IconButton, DialogActions, Button, FormControlLabel, FormGroup, Checkbox, Typography } from '@mui/material';
import Papa from 'papaparse';
import { IoClose } from "react-icons/io5";
import * as log from 'loglevel'
import { ScrollableDialogContent } from '../Components/ScrollableDialogContent.js';

const CsvUploaderPopup = ({ default_data, file, formValues, triggerCsvUploaderPopup, setTriggerCsvUploaderPopup, getToken }) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (file) {
      setSuccessMessage('');
      Papa.parse(file, {
        header: true,
        dynamicTyping: true,
        complete: (results) => {
          const headers = results.meta.fields;
          if (headers) {
            setFields(headers);
            setSelectedFields([]);
            setParsedData(results.data);
            setErrorMessage('');
            setSuccessMessage('');
          } else {
            setErrorMessage('Failed to parse fields from the CSV file.');
            setSuccessMessage('');
          }
        },
        error: (error) => {
          setErrorMessage(`Error parsing CSV file: ${error.message}`);
        },
      });
    }
  }, [file]);

  useEffect(() => {
    if (triggerCsvUploaderPopup) {
      if (triggerCsvUploaderPopup === 1) {
        setErrorMessage('');
        setSuccessMessage('');
        setIsOpen(true);
      }
    }
  }, [triggerCsvUploaderPopup]);

  const closeModal = () => {
    setTriggerCsvUploaderPopup(0);
    setIsOpen(false);
  };

  const handleFieldChange = (event) => {
    const { name, checked } = event.target;
    setSelectedFields((prevSelectedFields) =>
      checked ? [...prevSelectedFields, name] : prevSelectedFields.filter((field) => field !== name)
    );
  };

  const handleSubmit = async () => {
    const filteredData = parsedData.map(row => {
      const filteredRow = {};
      selectedFields.forEach(field => {
        filteredRow[field] = row[field];
      });
      return filteredRow;
    });

    setSuccessMessage('');
    setErrorMessage('');
    setIsLoading(true);

    // loop through filteredData
    var csvLines = []
    filteredData.forEach(row => {
      const csvLine = Papa.unparse([row], { header: false });
      csvLines.push(csvLine);
    });

    const formData = new FormData();
    formData.append('data', JSON.stringify(csvLines));

    // check if context is present in formValues
    var context = ''
    if (formValues['context']) {
      context = formValues['context']
    }
    formData.append('context', context);
    formData.append('filename', file.name)

    // check if dataset is present in formValues
    if (!formValues['dataset']) {
      setErrorMessage('Dataset name is required');
      return;
    }
    else {
      var dataset = formValues['dataset']
      // check if dataset is a string
      if (typeof dataset !== 'string') {
        dataset = dataset.value
      }
     
      formData.append('dataset', dataset);
    }

    var token = getToken();
    if (token === false) {
      return;
    }

    try {
      const response = await fetch(default_data.default_chat_service_url + '/train-with-array', {
        method: 'POST',
        body: formData,
        headers: {
          'x-access-tokens': `${token}`
        }
      });

      if (response.ok) {
        setIsLoading(false);
        const data = await response.json();
        if (data.status != 200) {
          setErrorMessage('Internal error, please contact customer support. [1]')
          return;
        }
        setSuccessMessage('CSV ingested successfully, you can close the modal now.');
      } else {
        setErrorMessage('Internal error, please contact customer support. [2]')
        log.error(`/csvuploaderpopup: 1: ${response}`);
      }
    } catch (error) {
      setErrorMessage('Internal error, please contact customer support.')
      log.error(`/csvuploaderpopup: handlesubmit: ${error.message}`);
      setIsLoading(false);
    }
  };

  return (
    <Dialog maxWidth='xl' fullWidth
      open={modalIsOpen}
      onClose={closeModal}
    >
      <DialogTitle>
        Select Fields
        <IconButton
          aria-label="close"
          onClick={closeModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <IoClose />
        </IconButton>
      </DialogTitle>
      <ScrollableDialogContent>
        {fields.length > 0 ? (
          <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
            <FormGroup>
              {fields.map((field) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      checked={selectedFields.includes(field)}
                      onChange={handleFieldChange}
                      name={field}
                    />
                  }
                  label={field}
                />
              ))}
            </FormGroup>
          </Box>
        ) : (
          <Typography>
            No fields found in the CSV file
          </Typography>
        )}
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        {successMessage && <p style={{ color: 'white' }}>{successMessage}</p>}
      </ScrollableDialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isLoading} variant="contained" color="primary">
          {isLoading ? 'Loading...' : 'Submit'}
        </Button>
        <Button onClick={closeModal} color="error">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CsvUploaderPopup;